import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn, downloadCsv, type CsvColumn } from '@/lib/utils';
import { Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { exportCutMessage, exportWasCut, withExport, type QueryParams } from './paging-export';

export function CsvExportButton<TResponse, TRow>({
  path,
  params,
  pickRows,
  columns,
  filename,
  disabled,
  size = 'sm',
  className,
}: {
  path: string;
  params: QueryParams;
  pickRows: (response: TResponse) => TRow[];
  columns: readonly CsvColumn<TRow>[];
  filename: string;
  disabled?: boolean;
  size?: 'sm' | 'default';
  className?: string;
}) {
  const toast = useToast();
  const [cut, setCut] = useState(false);

  const exportAll = useMutation({
    mutationFn: async () => pickRows(await get<TResponse>(`${path}${qs(withExport(params))}`)),
    onSuccess: (rows) => {
      downloadCsv(filename, rows, columns);
      const wasCut = exportWasCut(rows.length);
      setCut(wasCut);
      if (wasCut) toast.warning(tr('pharmacy', 'export_cut_short'), exportCutMessage());
    },
    onError: (error) => toast.error(tr('pharmacy', 'could_not_export'), describeError(error)),
  });

  return (
    <div className={cn('flex flex-col items-end gap-1', className)}>
      <Button
        type="button"
        variant="outline"
        size={size}
        disabled={disabled}
        loading={exportAll.isPending}
        title={tr('pharmacy', 'exports_the_whole_result_not_only_the_page_on')}
        onClick={() => {
          setCut(false);
          exportAll.mutate();
        }}
      >
        {!exportAll.isPending && <Download className="h-4 w-4" />}
        {tr('pharmacy', 'export_csv')}
      </Button>
      {cut && (
        <p role="status" className="max-w-xs text-end text-xs text-warning">
          {exportCutMessage()}
        </p>
      )}
    </div>
  );
}
